import { type ComponentType } from "react";

import type { CitationTarget, CitationsListTarget, DocumentTarget, EditTarget, SourceViewerTarget, WorklogTarget } from "@/contexts/InspectorContext";
import { lazyWithRetry } from "@/lib/lazyWithRetry";

/**
 * A self-contained renderer for one inspector tab `kind`. Components are
 * lazy-loadable so heavy viewers (pdfjs, docx-preview) stay out of the shell
 * bundle until a tab of that kind is opened.
 */
export interface PaneRenderer<T> {
  Component: ComponentType<{ payload: T; active: boolean }>;
  deriveTitle?: (payload: T) => string;
}

/**
 * Shared lazy reference so `document` and `citation` tabs use the exact same
 * React component type. Without this, switching a tab between the two kinds
 * would unmount/remount the viewer (two separate `lazy()` wrappers are distinct
 * component types to React), causing a redundant document re-fetch.
 */
const LazyDocumentPane = lazyWithRetry(() => import("@/components/workspace/panes/DocumentPane"));

/**
 * The modularity boundary: kind → renderer module, registered in exactly one
 * place. Adding a new pane kind is one union member (in `InspectorContext`),
 * one entry here, and one file under `./panes/`. The shell never switches on
 * `kind` inline and imports no pane internals.
 */
export const INSPECTOR_RENDERERS: {
  document: PaneRenderer<DocumentTarget>;
  citation: PaneRenderer<CitationTarget>;
  edit: PaneRenderer<EditTarget>;
  citations: PaneRenderer<CitationsListTarget>;
  worklog: PaneRenderer<WorklogTarget>;
  sources: PaneRenderer<SourceViewerTarget>;
} = {
  document: { Component: LazyDocumentPane },
  citation: { Component: LazyDocumentPane },
  edit: { Component: lazyWithRetry(() => import("@/components/workspace/panes/EditPane")) },
  citations: { Component: lazyWithRetry(() => import("@/components/workspace/CitationsTab")) },
  worklog: { Component: lazyWithRetry(() => import("@/components/workspace/panes/WorklogPane")) },
  sources: { Component: lazyWithRetry(() => import("@/components/workspace/panes/SourceViewerPane")) },
};
